/**
 * AiHelpDialog — help specific to AI generation features.
 * Opened from the AI gutter button popover "Help" item.
 */

import { useEffect } from 'react';
import { useUiStore } from '@/store/uiStore.ts';
import styles from './HelpDialog.module.css';

export function AiHelpDialog() {
  const open = useUiStore(s => s.aiHelpDialogOpen);
  const close = useUiStore(s => s.closeAiHelpDialog);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, close]);

  if (!open) return null;

  return (
    <div className={styles.backdrop} onClick={close}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <span className={styles.title}>AI Generation</span>
          <button className={styles.closeBtn} onClick={close}>&times;</button>
        </div>

        <div className={styles.body}>
          <div className={styles.intro}>
            Generate AI storyboard images, animated video, and character speech from your shots.
            Credits are only deducted on success.
          </div>
          <div className={styles.intro}>
            For relatively little expense, you can batch enhance every shot in your storyboard,
            replace key moments with video, or even assemble everything as animated videos.
          </div>

          {/* ── Glow Up ── */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Glow Up</div>
            <div className={styles.card}>
              <div className={styles.cardIcon}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path d="M10 2 L11.5 7.5 L17 9 L11.5 10.5 L10 16 L8.5 10.5 L3 9 L8.5 7.5 Z" fill="#d4a017" />
                </svg>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>One-click scene enhancement</div>
                <div className={styles.cardDesc}>
                  Glow Up enhances every shot in your current scene (or project)
                  using the best available image model — no options to configure.
                  The cost is calculated as shots &times; per-shot credits and shown
                  before you confirm. Results are auto-applied immediately with no
                  review step, so your storyboard lights up fast.
                </div>
              </div>
            </div>
          </div>

          {/* ── Batch Images ── */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Enhance Images</div>
            <div className={styles.card}>
              <div className={styles.cardIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z" />
                </svg>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>Enhance storyboard frames</div>
                <div className={styles.cardDesc}>
                  Transforms your shot thumbnails into stylized storyboard art using AI.
                  Choose a scope (current shot, scene, or entire project) and select an image model.
                  Each shot's metadata — framing, angle, time of day — is automatically
                  included in the prompt. You can edit prompts before generating.
                </div>
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10" /><polyline points="23 20 23 14 17 14" />
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" />
                </svg>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>Before / after review</div>
                <div className={styles.cardDesc}>
                  After generation, review each result with a before/after slider.
                  Accept the ones you like, reject the rest. Press <strong>E</strong> to
                  toggle between original thumbnails and enhanced images in the viewer.
                </div>
              </div>
            </div>
          </div>

          {/* ── 2 Shots→Video ── */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>2 Shots&#x2192;Video</div>
            <div className={styles.card}>
              <div className={styles.cardIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" /><polyline points="12 5 19 12 12 19" />
                </svg>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>Continuous transition video</div>
                <div className={styles.cardDesc}>
                  Generates a single AI video that spans two adjacent shots.
                  The first shot&rsquo;s image becomes the starting frame and the second
                  shot&rsquo;s image becomes the ending frame, creating a seamless animated
                  transition. During playback, both shots play as one combined video clip.
                  Choosing this menu item automatically marks the transition between the
                  current shot and the next shot as &ldquo;Continuous.&rdquo;
                </div>
              </div>
            </div>
          </div>

          {/* ── 1 Shot→Video ── */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>1 Shot&#x2192;Video</div>
            <div className={styles.card}>
              <div className={styles.cardIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <polygon points="10,8 16,12 10,16" fill="currentColor" stroke="none" />
                </svg>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>Animate a single shot</div>
                <div className={styles.cardDesc}>
                  Generates a short AI video clip from the current shot&rsquo;s image.
                  The video replaces the static thumbnail in the viewer during playback.
                  If the shot is already part of a continuous transition pair, generates
                  the transition video instead.
                </div>
              </div>
            </div>
          </div>

          {/* ── Transitions ── */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Transitions</div>
            <div className={styles.card}>
              <div className={styles.cardIcon}>&#x25C7;</div>
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>Transition types</div>
                <div className={styles.cardDesc}>
                  Click the diamond between two shots on the timeline to set a transition:
                  Cut (instant), Dissolve, Fade to Black, or Continuous (video).
                  These are applied during playback and in exported HTML.
                </div>
              </div>
            </div>
          </div>

          {/* ── Speech ── */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Add Speech</div>
            <div className={styles.card}>
              <div className={styles.cardIcon}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>Generate character dialogue</div>
                <div className={styles.cardDesc}>
                  Requires a screenplay attached to your project. Each character is
                  automatically assigned a distinct voice from eight available presets.
                  Generated speech clips are placed in the Dialog audio lane, timed to
                  the correct shot. You can optionally retime shots to match the dialogue
                  duration.
                </div>
              </div>
            </div>
          </div>

          {/* ── Quick Generation ── */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Quick Generation</div>
            <div className={styles.card}>
              <div className={styles.cardIcon}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path d="M10 2 L11.5 7.5 L17 9 L11.5 10.5 L10 16 L8.5 10.5 L3 9 L8.5 7.5 Z" fill="currentColor" />
                  <path d="M15 2 L15.5 3.5 L17 4 L15.5 4.5 L15 6 L14.5 4.5 L13 4 L14.5 3.5 Z" fill="currentColor" opacity="0.6" />
                </svg>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>AI menu</div>
                <div className={styles.cardDesc}>
                  The sparkle button to the left of the timeline opens a menu with quick
                  single-shot actions and batch dialogs. Quick items use the last-selected
                  model and show a confirm popover on the viewer.
                </div>
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardIcon}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z" />
                </svg>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardDesc}>
                  <strong>Enhance Shot</strong> (<strong>&#x2325;I</strong>) &mdash; enhance the current shot with AI.
                  Opens a confirm popover where you can edit the prompt before generating.
                </div>
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardIcon}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <polygon points="10,8 16,12 10,16" fill="currentColor" stroke="none" />
                </svg>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardDesc}>
                  <strong>1 Shot&#x2192;Video</strong> &mdash; animate the current shot into a
                  short video clip. If the shot is already part of a continuous transition pair,
                  generates the transition video instead.
                </div>
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardIcon}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" /><polyline points="12 5 19 12 12 19" />
                </svg>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardDesc}>
                  <strong>2 Shots&#x2192;Video</strong> &mdash; opens the continuous transition dialog
                  for the current shot and its right neighbor. Automatically sets the
                  transition type to Continuous if it isn&rsquo;t already. Requires a next
                  shot in the same scene.
                </div>
              </div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardIcon}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <div className={styles.cardContent}>
                <div className={styles.cardDesc}>
                  <strong>Add Speech</strong> (<strong>&#x2325;S</strong>) &mdash; type a line and pick a voice
                  to generate a single speech clip for the current shot.
                </div>
              </div>
            </div>
          </div>

          <div className={styles.divider} />

          {/* ── Credits ── */}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Credits</div>
            <div className={styles.card}>
              <div className={styles.cardIcon}>&#x1F4B3;</div>
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>Pay only for what works</div>
                <div className={styles.cardDesc}>
                  Credits are deducted when a generation completes successfully.
                  If a generation fails, your credits are automatically refunded.
                  Purchase credit packs from the account button in the transport bar.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
